import type { FormInstance } from '@arco-design/web-react/es/Form';

import { DEFAULT_SHARED_MEMORY, DEFAULT_SHARED_MEMORY_SIZE_GI, DEFAULT_SHARED_MEMORY_SIZE_MIN, SHARED_MEMORY_SIZE_UNIT } from './constants';
import { SHARED_MEMORY_FIELD } from './fieldKeys';
import type { SharedMemoryFormValues } from './types';

export { SHARED_MEMORY_FIELD };

/** 解析 sizeLimit 为 Gi 数值（兼容历史 Mi 写法） */
export function parseSharedMemorySizeGi(sizeLimit?: string, fallback = DEFAULT_SHARED_MEMORY_SIZE_GI): number {
  const raw = String(sizeLimit ?? '').trim();
  if (!raw) return fallback;
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*(Mi|Gi)?$/i);
  if (!match) return fallback;
  const num = parseFloat(match[1]);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  const unit = (match[2] ?? SHARED_MEMORY_SIZE_UNIT).toLowerCase();
  if (unit === 'mi') {
    const gi = num / 1024;
    return gi >= DEFAULT_SHARED_MEMORY_SIZE_MIN ? Math.round(gi) : DEFAULT_SHARED_MEMORY_SIZE_MIN;
  }
  return num;
}

/** 将 Gi 数值格式化为 sizeLimit（固定 Gi 单位） */
export function formatSharedMemorySizeGi(gi: number): string {
  const normalized = Number.isFinite(gi) ? gi : DEFAULT_SHARED_MEMORY_SIZE_GI;
  return `${normalized}${SHARED_MEMORY_SIZE_UNIT}`;
}

/** 从 containerDeployConfig.sharedMemory 解析为表单值 */
export function parseSharedMemoryFromDeployConfig(
  sharedMemory?: { enabled?: boolean; sizeLimit?: string } | Record<string, unknown> | null,
): SharedMemoryFormValues | undefined {
  if (!sharedMemory || typeof sharedMemory !== 'object') return undefined;
  const sm = sharedMemory as { enabled?: boolean; sizeLimit?: string };
  const enabled = sm.enabled ?? !!String(sm.sizeLimit ?? '').trim();
  return {
    ...DEFAULT_SHARED_MEMORY,
    enabled,
    sizeLimit: String(sm.sizeLimit ?? '').trim() || DEFAULT_SHARED_MEMORY.sizeLimit,
  };
}

/** 将 sizeLimit 提升到不低于 minGi（滑条只改显示值，提交仍读表单） */
export function clampSharedMemoryConfigToMin(config: SharedMemoryFormValues | undefined | null, minGi?: number): SharedMemoryFormValues {
  const resolvedMin = minGi && minGi > 0 ? minGi : DEFAULT_SHARED_MEMORY_SIZE_MIN;
  const gi = parseSharedMemorySizeGi(config?.sizeLimit, resolvedMin);
  return {
    enabled: true,
    ...(config || {}),
    sizeLimit: formatSharedMemorySizeGi(Math.max(gi, resolvedMin)),
  };
}
/** 是否填写了有效的共享内存大小（启用态由外层模块勾选控制） */
export function hasMeaningfulSharedMemoryConfig(sharedMemory?: SharedMemoryFormValues | null): boolean {
  if (!sharedMemory) return false;
  return Boolean(String(sharedMemory.sizeLimit ?? '').trim());
}

/** 选镜像后写入共享内存表单字段 */
export function applySharedMemoryFromDeployConfig(
  form: FormInstance,
  config?: Record<string, unknown> | null,
  options?: { field?: string },
) {
  const field = options?.field ?? SHARED_MEMORY_FIELD;
  const parsed = parseSharedMemoryFromDeployConfig(config?.sharedMemory as { enabled?: boolean; sizeLimit?: string } | undefined);
  if (!parsed) {
    form.setFieldValue(field, undefined);
    return;
  }
  form.setFieldValue(field, parsed);
}

/** 清空共享内存相关表单字段 */
export function clearSharedMemoryFromForm(form: FormInstance, options?: { field?: string }) {
  form.setFieldValue(options?.field ?? SHARED_MEMORY_FIELD, undefined);
}

/** 从表单读取共享内存配置 */
export function readSharedMemoryFromFormValues(
  values: Record<string, unknown>,
  field = SHARED_MEMORY_FIELD,
): SharedMemoryFormValues | undefined {
  const raw = values[field];
  if (!raw || typeof raw !== 'object') return undefined;
  return { ...DEFAULT_SHARED_MEMORY, ...(raw as SharedMemoryFormValues) };
}

/** 从 API system.devices.sharedMemory 反推表单值 */
export function sharedMemoryFromSystemDevices(devices?: Record<string, unknown> | null): SharedMemoryFormValues | undefined {
  if (!devices || typeof devices !== 'object') return undefined;
  const sharedMemory = devices.sharedMemory as { size?: string } | undefined;
  const size = String(sharedMemory?.size ?? '').trim();
  if (!size) return undefined;
  return {
    enabled: true,
    sizeLimit: size,
  };
}
